/**
 * The only place in the application that calls `fetch`.
 *
 * Responsibilities, in order:
 *   1. attach the bearer token and the tenant header;
 *   2. attach a request id so a client-side report can be matched to a server log line;
 *   3. unwrap the `{ success, data, meta }` envelope so callers receive `data` directly;
 *   4. raise a typed `ApiError` carrying the stable code, message and field details;
 *   5. on 401, refresh once — as a single shared promise — then replay the request.
 */

import type {
  ApiErrorCode,
  Envelope,
  Page,
  PaginationMeta,
  TokenPair,
} from '@/types/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const TENANT_HEADER = import.meta.env.VITE_TENANT_HEADER ?? 'X-Tenant';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: Record<string, string[]> | undefined;
  readonly requestId: string | null;

  constructor(args: {
    code: ApiErrorCode;
    message: string;
    status: number;
    details?: Record<string, string[]>;
    requestId?: string | null;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
    this.requestId = args.requestId ?? null;
  }

  /** True when the caller should stop rather than retry: the request itself is wrong. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

/* -------------------------------------------------------------------------- */
/* Session state                                                              */
/* -------------------------------------------------------------------------- */

interface Session {
  access: string | null;
  refresh: string | null;
  tenantSlug: string | null;
}

const session: Session = { access: null, refresh: null, tenantSlug: null };

/** Callbacks the auth store registers so it learns about refresh outcomes. */
let onTokensRefreshed: ((tokens: TokenPair) => void) | null = null;
let onSessionExpired: (() => void) | null = null;

export function configureSession(next: Partial<Session>): void {
  Object.assign(session, next);
}

export function getSession(): Readonly<Session> {
  return session;
}

export function onRefresh(handler: (tokens: TokenPair) => void): void {
  onTokensRefreshed = handler;
}

export function onExpiry(handler: () => void): void {
  onSessionExpired = handler;
}

/* -------------------------------------------------------------------------- */
/* Refresh, single-flight                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Concurrent 401s must not each trigger a refresh: with rotation enabled the first
 * would invalidate the token the others are about to present, and the session would
 * die on a burst of parallel requests. They share one in-flight promise instead.
 */
let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    if (!session.refresh) throw new Error('No refresh token');

    const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      method: 'POST',
      headers: buildHeaders({ json: true, authenticated: false }),
      body: JSON.stringify({ refresh: session.refresh }),
    });

    if (!response.ok) throw new Error('Refresh rejected');

    const body = (await response.json()) as Envelope<TokenPair>;
    if (!body.success) throw new Error(body.error.message);

    session.access = body.data.access;
    // Rotation is on, so the refresh token changes too. Keeping the old one would
    // present a blacklisted token on the next refresh.
    if (body.data.refresh) session.refresh = body.data.refresh;

    onTokensRefreshed?.(body.data);
    return body.data.access;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/* -------------------------------------------------------------------------- */
/* Request                                                                    */
/* -------------------------------------------------------------------------- */

function buildHeaders(options: {
  json: boolean;
  authenticated: boolean;
  extra?: HeadersInit;
}): Headers {
  const headers = new Headers(options.extra);

  if (options.json) headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');
  headers.set('X-Request-ID', crypto.randomUUID());

  if (session.tenantSlug) headers.set(TENANT_HEADER, session.tenantSlug);
  if (options.authenticated && session.access) {
    headers.set('Authorization', `Bearer ${session.access}`);
  }

  return headers;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Set for endpoints that must not carry credentials, e.g. login. */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }

  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

async function parseError(response: Response): Promise<ApiError> {
  let code: ApiErrorCode = 'internal_error';
  let message = response.statusText || 'The request failed.';
  let details: Record<string, string[]> | undefined;
  let requestId: string | null = response.headers.get('X-Request-ID');

  try {
    const body = (await response.json()) as Envelope<unknown>;
    if (body && body.success === false) {
      code = body.error.code;
      message = body.error.message;
      details = body.error.details;
      requestId = body.meta?.request_id ?? requestId;
    }
  } catch {
    // A non-JSON body (a proxy error page, a truncated response) leaves the defaults,
    // which are already accurate enough to show the user.
  }

  return new ApiError({ code, message, status: response.status, details, requestId });
}

async function performRequest<T>(
  path: string,
  options: RequestOptions,
  isRetry: boolean,
): Promise<{ data: T; pagination: PaginationMeta | null }> {
  const hasBody = options.body !== undefined;
  // A multipart upload carries its own Content-Type (with a boundary the browser sets), so
  // it must not be JSON-encoded nor have application/json forced onto it.
  const isMultipart = hasBody && options.body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers: buildHeaders({
        json: hasBody && !isMultipart,
        authenticated: !options.anonymous,
      }),
      body: hasBody
        ? isMultipart
          ? (options.body as FormData)
          : JSON.stringify(options.body)
        : undefined,
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiError({
      code: 'network_error',
      message: 'The server could not be reached. Check your connection and try again.',
      status: 0,
      ...(cause instanceof Error ? {} : {}),
    });
  }

  if (response.status === 401 && !isRetry && !options.anonymous && session.refresh) {
    try {
      await refreshAccessToken();
      return performRequest<T>(path, options, true);
    } catch {
      onSessionExpired?.();
      throw new ApiError({
        code: 'authentication_failed',
        message: 'Your session has ended. Please sign in again.',
        status: 401,
      });
    }
  }

  if (response.status === 204) {
    return { data: undefined as T, pagination: null };
  }

  if (!response.ok) throw await parseError(response);

  const body = (await response.json()) as Envelope<T>;
  if (!body.success) {
    throw new ApiError({
      code: body.error.code,
      message: body.error.message,
      status: response.status,
      details: body.error.details,
      requestId: body.meta?.request_id,
    });
  }

  return { data: body.data, pagination: body.meta?.pagination ?? null };
}

/** Perform a request and return the unwrapped `data`. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { data } = await performRequest<T>(path, options, false);
  return data;
}

/** Perform a list request, returning results alongside their pagination metadata. */
export async function requestPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Page<T>> {
  const { data, pagination } = await performRequest<T[]>(path, options, false);
  return { results: data, pagination };
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  list: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    requestPage<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T = void>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
